import React, { useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import UploadIcon from '@mui/icons-material/Upload';
import { DEFAULT_BADGE_ICON } from '../services/progressionAdminApi';
import { uploadFile } from '../services/filesApi';
import { ClickableBadgeIcon } from './BadgeIconLightbox';
import { ADMIN_BADGE_ICON_SIZE, isDefaultBadgeIcon, resolveBadgeIconSrc } from './badgeIconUtils';

export { ADMIN_BADGE_ICON_SIZE, isDefaultBadgeIcon, resolveBadgeIconSrc } from './badgeIconUtils';

type BadgeIconFieldProps = {
  icon: string;
  onChange: (icon: string) => void;
  label?: string;
};

export const BadgeIconField: React.FC<BadgeIconFieldProps> = ({ icon, onChange, label = 'Icon' }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const src = resolveBadgeIconSrc(icon);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const res = await uploadFile(file, { tags: ['badge'] });
      const fileId = res.data?.fileId;
      if (!fileId) {
        throw new Error('Upload succeeded but no file id returned');
      }
      onChange(`/api/v1/files/${fileId}/serve`);
    } catch (err: any) {
      setError(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Box display="flex" alignItems="center" gap={2}>
        <Box
          sx={{
            width: ADMIN_BADGE_ICON_SIZE,
            height: ADMIN_BADGE_ICON_SIZE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'transparent',
          }}
        >
          {src ? (
            <ClickableBadgeIcon icon={icon} title="Badge icon preview" />
          ) : (
            <EmojiEventsIcon color="action" />
          )}
        </Box>
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Button
            size="small"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            Upload image
          </Button>
          {!isDefaultBadgeIcon(icon) ? (
            <Button size="small" onClick={() => onChange(DEFAULT_BADGE_ICON)}>
              Use default icon
            </Button>
          ) : null}
        </Box>
        <input ref={inputRef} type="file" accept="image/*,.svg" hidden onChange={handleUpload} />
      </Box>
      {error ? (
        <Typography variant="caption" color="error" display="block" mt={0.5}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
};
